function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useMemo } from 'react';
import { createInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';
import resourcesToBackend from 'i18next-resources-to-backend';
import get from 'lodash.get';
import { useTranslation as defaultUseTranslation } from 'react-i18next';
import { useOnlyLocale } from '@adsk/alloy-react-locale';
import { useCheckSuspenseSetup } from './hooks/useSuspense';
import config from './i18next.config';
const LOCALE_LAZY_LOAD_ERROR = 'localeLazyImporter_catch';
const formatResources = resources => {
  const resourcesFormatted = Object.entries(resources).reduce((rFormatted, _ref) => {
    let [key, valueObj] = _ref;
    if (typeof valueObj === 'string') {
      rFormatted[key] = valueObj;
    } else if (valueObj.message) {
      // Need to reformat the locales because i18next doesn't like the description
      // that we sometimes add to help the translators out.
      rFormatted[key] = get(valueObj, 'message', '');
    } else {
      rFormatted[key] = formatResources(valueObj);
    }
    return rFormatted;
  }, {});
  return resourcesFormatted;
};

// creates an importer for an app/library's locale files, create a separate instance for each app or library
const createLocaleLazyImporter = _ref2 => {
  let {
    getLocale
  } = _ref2;
  /**
   * Lazily imports translated strings for a language, then passes the result back to i18next.
   * @param language IETF BCP 47 language tag
   */
  const localeLazyImporter = (language, namespace, callback) => {
    getLocale(language, namespace).then(resources => {
      const resourcesFormatted = formatResources(resources);
      callback(null, resourcesFormatted);
    }).catch(error => {
      // No localization for this language, i18next will falling back to less specific locale...
      error.type = LOCALE_LAZY_LOAD_ERROR;
      callback(error, null);
    });
  };
  return localeLazyImporter;
};

// creates an instance if i18next, need one for every locale importer (one per app)
const createI18nInstance = _ref3 => {
  let {
    localeLazyImporter,
    translationFormat = 'default',
    plugins = [],
    initOptions = config
  } = _ref3;
  let instance = createInstance();
  if (translationFormat === 'ICU') {
    instance = instance.use(ICU);
  }
  plugins.forEach(plugin => {
    instance.use(plugin);
  });
  instance.use(LanguageDetector).use(resourcesToBackend(localeLazyImporter)).init(initOptions, errors => {
    if (errors) {
      errors.forEach(error => {
        if (error && error.type === LOCALE_LAZY_LOAD_ERROR) {
          // don't pollute log, this is expected if locale files are less specific
          // example: `es.json` instead of `es-ES.json`
          return;
        }
        console.error(error);
      });
    }
  });
  return instance;
};

// creates instance specific versions of the useTranslation hook and withTranslation HOC
// should have one instance for a library and one for an app
const createUseTranslation = _ref4 => {
  let {
    i18nInstance,
    internalUsage
  } = _ref4;
  const useTranslation = (namespace, options) => {
    const {
      locale
    } = useOnlyLocale();
    useCheckSuspenseSetup({
      internalUsage
    });

    /**
     * Order of language preference:
     * 1. user provided via options.lng (this probably won't be used)
     * 2. locale returned from locale provider
     * 3. locale returned from i18next-browser-languagedetector
     */
    const translationOptions = useMemo(() => options !== null && options !== void 0 && options.lng ? options : _objectSpread(_objectSpread({}, options), {}, {
      lng: locale
    }), [locale, options]);
    return defaultUseTranslation(namespace, _objectSpread({
      i18n: i18nInstance
    }, translationOptions));
  };
  const withTranslation = () => Component => {
    const Wrapper = ownProps => {
      const {
        t
      } = useTranslation();
      const injectProps = {
        t
      };
      const combinedProps = _objectSpread(_objectSpread({}, ownProps), injectProps);
      return /*#__PURE__*/React.createElement(Component, combinedProps);
    };
    return Wrapper;
  };
  return {
    useTranslation,
    withTranslation
  };
};
export const initLocalization = _ref5 => {
  let {
    getLocale,
    translationFormat,
    plugins,
    initOptions,
    internalUsage = false
  } = _ref5;
  const lazyLocaleImporter = createLocaleLazyImporter({
    getLocale
  });
  const i18nInstance = createI18nInstance({
    localeLazyImporter: lazyLocaleImporter,
    translationFormat,
    plugins,
    initOptions,
    internalUsage
  });
  return createUseTranslation({
    i18nInstance,
    internalUsage
  });
};
//# sourceMappingURL=localization.js.map